import compareVersions from 'compare-versions'
import moment from 'moment-timezone'
import Base from '@/resources/Base'
import { formatFromNow, getRuntimeText } from '@/utilities/datetime.helper'
import { getHostTrailRiskLevel } from '@/utilities/riskscore'
import { AgentStateEnum, HostStatusEnum } from '@/constants/host-status.constants'
import { NANOSECONDS_TO_MILLISECONDS, INACTIVE_AGENT_HOURS } from '@/constants/datetime.constants'
import { ipv4Re, ipv6Re, windowsDHCPFailRe } from '@/utilities/regex/ipaddress'
import { getContainerNameLabel } from '@/utilities/agent.helper'
import _ from 'lodash'

export default class Container extends Base {
  constructor (uuid = null, attributes = {}, options = {}) {
    super(uuid, attributes, options)
    this.display = true
    this.discovered = false
    this.upgradeable = false
    this.needsAttention = false
    this.upgradeText = 'Latest'
    this.setContainerInfo(this)
  }

  setContainerInfo (containerInfo) {
    // managed | unmanaged | unmanaged and name is null
    containerInfo.containerId = containerInfo.containerId || containerInfo.name || ''
    containerInfo.aggregatedRiskScore = containerInfo.aggregatedRiskScore || 0

    // convert createTime to ms if necessary (backend should be sending everything as nanosec)
    if (containerInfo.timestamp) {
      if (containerInfo.timestamp && !moment(containerInfo.timestamp).isValid()) {
        containerInfo.startTime = containerInfo.timestamp * NANOSECONDS_TO_MILLISECONDS
      }

      this.setUpTime(containerInfo.startTime)
    } else {
      containerInfo.startTime = NaN
      this.upTime = 'N/A'
    }

    this.setTags(containerInfo.tagMap)
    this.setContainerLabel(containerInfo.containerId)
    this.checkUpgradeable(containerInfo.agentVersion, this.iqhubVersion)
    this.setNeedsAttention(containerInfo)
    this.setDiscovery(containerInfo.discoveryAccount)
    this.setAtRisk(this.numProgressions)
  }

  setContainerLabel (label) {
    this.containerLabel = getContainerNameLabel(label)
  }

  getContainerLabel () {
    return this.containerLabel
  }

  setNeedsAttention () {
    if (this.managed === false || this.upgradeable || this.numProgressions > 0) {
      this.needsAttention = true
    }
  }

  setUpTime (createdTime) {
    if (createdTime) {
      let uptime = formatFromNow(createdTime).split(' ')[0]
      this.upTime = getRuntimeText(uptime)
    }
  }

  /**
   *
   * For 2021 Q2, only AWS is supported. Discovered == true => through aws
   *
   * @param {*} acct
   *  Discovery Account ID
   */
  setDiscovery (acct) {
    if (!_.isEmpty(acct)) {
      if (this.discovered) {
        switch (this.discoveryAccountType) {
          case 'AZURE':
            this.source = 'AZURE'
            break
          case 'GCP':
            this.source = 'GCP'
            break
          default:
            this.source = 'AWS'
        }
      } else {
        this.discovered = true
        if (this.k8sType.includes('GCP')) {
          this.source = 'GCP'
        } else if (this.k8sType.includes('AZURE')) {
          this.source = 'AZURE'
        } else {
          this.source = 'AWS'
        }
      }
      if (this.k8s) {
        if (this.k8sType.includes('GKE')) {
          this.source = 'GKE'
        } else if (this.k8sType.includes('AKS')) {
          this.source = 'AKS'
        } else {
          this.source = 'EKS'
        }
      }
    } else if (this.k8s) {
      if (this.k8sType.includes('GKE')) {
        this.source = 'GKE'
      } else if (this.k8sType.includes('AKS')) {
        this.source = 'AKS'
      } else {
        this.source = 'EKS'
      }
    } else {
      this.source = 'CONFLUERA'
    }
  }

  getState () {
    return this.state
  }

  setState (status, score, lastStatusChangeTime) {
    if (status === AgentStateEnum.ONLINE && score === 0) {
      this.state = HostStatusEnum.properties.safe.state
    } else if (status === AgentStateEnum.ONLINE && score > 0) {
      this.state = HostStatusEnum.properties.at_risk.state
    } else if (status === AgentStateEnum.DECOMMISSIONED) {
      this.state = HostStatusEnum.properties.decommissioned.state
    } else {
      this.state = HostStatusEnum.properties.offline.state
      this.setDecommissionEligibility(status, lastStatusChangeTime)
    }
  }

  setLocalIP (privateIPs = []) {
    this.localIPs = []
    if (!_.isEmpty(privateIPs)) {
      privateIPs.forEach(ip => {
        if (ipv4Re.test(ip) && !windowsDHCPFailRe.test(ip)) {
          this.localIPs.push(ip)
        }
      })
    }
    if (this.localIPs.length === 0) {
      if (!_.isEmpty(privateIPs)) {
        privateIPs.forEach(ip => {
          if (ipv6Re.test(ip)) {
            this.localIPs.push(ip)
          }
        })
      }
    }
  }

  setRiskLevel (riskScore) {
    this.riskLevel = getHostTrailRiskLevel(riskScore)
  }

  setAtRisk (progressions) {
    this.atRisk = progressions > 0
  }

  /**
   *
   * Host is eligible to be decommissioned if Status is OFFLINE
   *    && lastStatusChangeTime is > 24hrs
   */
  setDecommissionEligibility (status, lastStatusEventTimestamp) {
    if (!moment(lastStatusEventTimestamp).isValid()) {
      lastStatusEventTimestamp = lastStatusEventTimestamp * NANOSECONDS_TO_MILLISECONDS
    }
    let timeDelta = moment().diff(moment(lastStatusEventTimestamp), 'hours')
    if (status === AgentStateEnum.OFFLINE && timeDelta > INACTIVE_AGENT_HOURS) {
      this.decommissionEligibile = true
      this.state = HostStatusEnum.properties.decommissionable.state
    }
  }

  setTags (tags) {
    this.tags = _.chain(tags)
      .values()
      .uniq()
      .omitBy(_.isNil)
      .omitBy(_.isEmpty)
      .values()
      .value()
  }

  checkUpgradeable (containerVersion, iqhubVersion) {
    if (
      this.containerStatus === AgentStateEnum.OFFLINE ||
      this.containerStatus === AgentStateEnum.DECOMMISSIONED ||
      this.containerStatus === AgentStateEnum.DECOMMISSIONABLE ||
      _.isEmpty(iqhubVersion)
    ) {
      this.upgradeText = 'Offline'
      return
    }
    if (_.isEmpty(containerVersion)) {
      this.upgradeable = true
      this.upgradeText = 'Upgradeable'
      return
    }

    let containerVersionMajorIdx = containerVersion.indexOf('.', containerVersion.indexOf('.') + 1)
    let containerVersionCompare = containerVersion.substring(0, containerVersionMajorIdx)

    let iqhubVersionMajorIdx = iqhubVersion.indexOf('.', iqhubVersion.indexOf('.') + 1)
    let iqhubVersionCompare = iqhubVersion.substring(0, iqhubVersionMajorIdx)

    if (!_.isEmpty(containerVersion) && compareVersions.compare(containerVersionCompare, iqhubVersionCompare, '<')) {
      this.upgradeable = true
      this.upgradeText = 'Upgradeable'
    }
  }
}



// WEBPACK FOOTER //
// ./src/resources/models/Container.js